import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Progress } from "@/components/ui/progress";
import { BaseActionProps } from "@/components/common/adCard/types";
import { toast } from "sonner";
import { useState } from "react";
import { LucideAlertCircle, LucideCheckCircle } from "lucide-react";
import { withCacheBuster } from "../Media";
import posthog from "posthog-js";
import { RiDownloadLine } from "@remixicon/react";

// Helper function to get file extension from Content-Type or URL
const getFileExtension = (contentType: string | null, url: string): string => {
  // First, try to get extension from Content-Type header
  if (contentType) {
    const mimeToExt: Record<string, string> = {
      "image/jpeg": ".jpg",
      "image/jpg": ".jpg",
      "image/png": ".png",
      "image/gif": ".gif",
      "image/webp": ".webp",
      "image/svg+xml": ".svg",
      "video/mp4": ".mp4",
      "video/webm": ".webm",
      "video/quicktime": ".mov",
      "video/x-msvideo": ".avi",
      "video/x-matroska": ".mkv",
    };

    // Check for exact match
    if (mimeToExt[contentType]) {
      return mimeToExt[contentType];
    }

    // Check for partial match (e.g., "image/jpeg; charset=utf-8")
    const baseType = contentType.split(";")[0].trim();
    if (mimeToExt[baseType]) {
      return mimeToExt[baseType];
    }
  }

  // Fallback: try to extract extension from URL
  try {
    const urlPath = url.split("?")[0].split("#")[0];
    const match = urlPath.match(/\.([a-zA-Z0-9]{1,5})$/);
    if (match && match[1]) {
      return `.${match[1].toLowerCase()}`;
    }
  } catch {
    // Ignore URL parsing errors
  }

  // Default fallback
  return ".jpg";
};

// Extracted reusable download helper
export const downloadMedia = async (
  url: string,
  type?: string,
  aspectRatio?: string,
  accountName?: string,
) => {
  if (!url) return;

  const toastId = `download-${Date.now()}`;
  let progress = 0;

  const updateProgressToast = (percent: number) => {
    toast(
      <div className="space-y-2 min-h-[64px] flex flex-col justify-center w-full rounded-[12px]">
        <h4 className="font-medium leading-none">Downloading...</h4>
        <Progress
          value={percent}
          className="w-full"
          indicatorClassName="bg-[#FF4500] transition-none"
        />
        <p className="text-sm text-muted-foreground">{percent}% complete</p>
      </div>,
      {
        id: toastId,
        duration: Infinity,
      },
    );
  };

  try {
    progress = 0;
    updateProgressToast(progress);
    const buster = String(Date.now());
    const cacheBustedUrl = withCacheBuster(url, buster);
    const response = await fetch(cacheBustedUrl);
    const reader = response.body?.getReader();
    const contentLength = Number(response.headers.get("Content-Length")) || 0;
    const contentType = response.headers.get("Content-Type");

    if (!reader) throw new Error("Failed to initialize download");

    let receivedLength = 0;
    const chunks: Uint8Array[] = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      chunks.push(value);
      receivedLength += value.length;

      const percent = contentLength
        ? Math.round((receivedLength / contentLength) * 100)
        : 0;
      progress = percent;
      updateProgressToast(progress);
    }

    const blob = new Blob(chunks as BlobPart[], {
      type: contentType || undefined,
    });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;

    // Get file extension from Content-Type or URL
    const extension = getFileExtension(contentType, url);

    // Generate filename with aspect ratio and timestamp
    let safeName: string;

    if (aspectRatio) {
      const now = new Date();
      const timestamp = now
        .toISOString()
        .replace(/[-:]/g, "")
        .replace("T", "-")
        .substring(0, 15); // YYYYMMDD-HHmmss format

      safeName = `getcrux_${aspectRatio.replace(":", "x")}_${timestamp}${extension}`;
    } else if (accountName) {
      // Format: {Account name} {Time stamp (date&time)}, e.g. 25-10-25-22:11, hours:mins
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      const localTimestamp = [
        pad(now.getDate()), // day
        pad(now.getMonth() + 1), // month (0-based)
        pad(now.getFullYear() % 100), // two-digit year
        `${pad(now.getHours())}${pad(now.getMinutes())}`, // hourmins
      ].join("-");
      safeName = `${accountName} ${localTimestamp}${extension}`;
    } else {
      // Use legacy format when aspect ratio is not provided
      const rawName = url.split("/").pop() || type || "media";
      const hasSuffix = /\.[a-zA-Z0-9]{1,5}(?:$|[?#])/i.test(rawName);
      safeName = hasSuffix ? rawName : `${rawName}${extension}`;
    }

    link.download = safeName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    // success toast
    toast(
      <div className="space-y-2 min-h-[64px] flex flex-col w-full rounded-[12px]">
        <div className="flex items-center">
          <LucideCheckCircle size={16} className="shrink-0 mr-1 text-[#FF4500]" />
          <h4 className="font-medium leading-none text-[#FF4500]">Download completed!</h4>
        </div>
        <p className="text-sm text-muted-foreground">Your media has been saved.</p>
      </div>,
      {
        id: toastId,
        duration: 2000,
      },
    );
  } catch (error) {
    console.error(error);
    toast(
      <div className="space-y-2 min-h-[64px] flex flex-col w-full rounded-[12px]">
        <div className="flex items-center">
          <LucideAlertCircle size={16} color="red" className="mr-1 shrink-0" />
          <h4 className="font-medium leading-none text-red">Failed to download media.</h4>
        </div>
        <p className="text-sm text-muted-foreground">Please try again.</p>
      </div>,
      {
        id: toastId,
        duration: 3000,
      },
    );
  }
};

interface DownloadActionProps extends BaseActionProps {
  url?: string;
  type?: string;
  aspectRatio?: string;
  accountName?: string;
}

const DownloadAction = ({ url, type, aspectRatio, accountName }: DownloadActionProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const handleClick = async () => {
    if (!url) return;

    // Track download click event
    posthog.capture("ad_download_clicked", {
      creative_type: type || "unknown",
      aspect_ratio: aspectRatio || null,
      media_url: url,
    });

    setIsDownloading(true);
    await downloadMedia(url, type, aspectRatio, accountName);
    setIsDownloading(false);
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            data-ph-click
            data-attr={`ad-download`}
            data-ph-type={type}
            className={`h-[30px] w-[30px] flex items-center justify-center pointer-events-auto squircle-mask squircle-smooth-[1] squircle-radius-[8px] bg-[#00000099] backdrop-blur-sm hover:bg-[#000000CC] font-['Helvetica_Neue'] text-[12px] font-normal leading-[16px] text-[#fff] p-[6px] rounded-[8px] transition-opacity cursor-pointer`}
            onClick={() => void handleClick()}
            disabled={isDownloading}
          >
            <RiDownloadLine size={14} className="text-white shrink-0" />
          </button>
        </TooltipTrigger>
        <TooltipPrimitive.Portal>
          <TooltipContent
            side="top"
            align="start"
            alignOffset={20}
            avoidCollisions
            collisionPadding={20}
          >
            <span>Download</span>
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  );
};

export default DownloadAction;
